import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Send, Wand2, AlertTriangle, CalendarPlus, CalendarMinus, Check, X, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLES = [
  'Sono disponibile il 20 dicembre dalle 9 alle 13',
  'Tutti i martedì e giovedì di gennaio dalle 14 alle 18',
  'Cancella la disponibilità di venerdì prossimo',
];

function fmtDate(d) {
  try { return format(parseISO(d), 'EEE d MMM', { locale: it }); } catch { return d; }
}

/**
 * Assistente AI per la definizione delle disponibilità in linguaggio naturale.
 * Flusso: messaggio -> anteprima (nessuna modifica) -> conferma esplicita -> applicazione.
 * - doctorId assente => modalità dottore (token). Presente => modalità staff.
 */
export function AvailabilityChat({ doctorId, onApplied }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || sending) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: message }]);
    setSending(true);
    try {
      const body = { message };
      if (doctorId) body.doctor_id = doctorId;
      const res = await axios.post(`${API}/doctor/availability-chat`, body);
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.reply, plan: res.data.plan_id ? res.data : null, dismissed: false, applied: false }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: err.response?.data?.detail || 'Errore dell\'assistente, riprova tra poco.', plan: null }]);
    } finally {
      setSending(false);
    }
  };

  const confirmPlan = async (msgIndex, plan) => {
    if (confirmingId) return;
    setConfirmingId(plan.plan_id);
    try {
      const body = { plan_id: plan.plan_id };
      if (doctorId) body.doctor_id = doctorId;
      const res = await axios.post(`${API}/doctor/availability-chat/confirm`, body);
      setMessages(prev => prev.map((m, i) => i === msgIndex ? { ...m, applied: true } : m));
      const parts = [];
      if (res.data.created > 0) parts.push(`${res.data.created} slot creati`);
      if (res.data.deleted > 0) parts.push(`${res.data.deleted} slot cancellati`);
      toast.success(parts.length ? parts.join(', ') : 'Disponibilità aggiornata');
      setMessages(prev => [...prev, { role: 'assistant', text: `Fatto! ${parts.join(' e ') || 'Disponibilità aggiornata'}. Il calendario è stato aggiornato.`, plan: null }]);
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'applicazione delle modifiche');
    } finally {
      setConfirmingId(null);
    }
  };

  const dismissPlan = (msgIndex) => {
    setMessages(prev => prev.map((m, i) => i === msgIndex ? { ...m, dismissed: true } : m));
  };

  return (
    <Card className="mb-6" data-testid="availability-chat-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
          <Wand2 size={16} className="text-[#238A92]" />
          Assistente Disponibilità
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Scrivi la tua disponibilità in linguaggio naturale: l'assistente prepara gli slot e li applica solo dopo la tua conferma.
        </p>
      </CardHeader>
      <CardContent>
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {EXAMPLES.map(ex => (
              <button
                key={ex}
                type="button"
                onClick={() => send(ex)}
                className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--border))] text-muted-foreground hover:border-[#238A92] hover:text-[#238A92] transition-colors duration-150"
                data-testid="chat-example-chip"
              >
                {ex}
              </button>
            ))}
          </div>
        )}

        {messages.length > 0 && (
          <div ref={scrollRef} className="space-y-3 max-h-80 overflow-y-auto mb-3 pr-1" data-testid="availability-chat-messages">
            {messages.map((m, i) => (
              <div key={`msg-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${
                  m.role === 'user'
                    ? 'bg-[#238A92] text-white'
                    : 'bg-[hsl(var(--secondary))] text-[#1F2A37]'
                }`}>
                  <p className="whitespace-pre-wrap">{m.text}</p>

                  {/* Anteprima piano */}
                  {m.plan && !m.dismissed && (
                    <div className="mt-3 rounded-lg border border-[hsl(var(--border))] bg-white p-3 space-y-2" data-testid="chat-plan-preview">
                      {m.plan.create_count > 0 && (
                        <div>
                          <p className="text-xs font-semibold flex items-center gap-1 text-[#0F5E66]">
                            <CalendarPlus size={13} /> {m.plan.create_count} slot da creare
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {m.plan.to_create.slice(0, 8).map(e => (
                              <Badge key={e.data} variant="outline" className="text-[10px] font-normal capitalize">
                                {fmtDate(e.data)}: {e.orari[0]}–{e.orari[e.orari.length - 1]} ({e.orari.length})
                              </Badge>
                            ))}
                            {m.plan.to_create.length > 8 && (
                              <Badge variant="outline" className="text-[10px] font-normal">+{m.plan.to_create.length - 8} giorni</Badge>
                            )}
                          </div>
                        </div>
                      )}
                      {m.plan.delete_count > 0 && (
                        <div>
                          <p className="text-xs font-semibold flex items-center gap-1 text-red-700">
                            <CalendarMinus size={13} /> {m.plan.delete_count} slot da cancellare
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {m.plan.to_delete.slice(0, 10).map(s => (
                              <Badge key={s.id} variant="outline" className="text-[10px] font-normal capitalize">{fmtDate(s.data)} {s.ora_inizio}</Badge>
                            ))}
                            {m.plan.to_delete.length > 10 && (
                              <Badge variant="outline" className="text-[10px] font-normal">+{m.plan.to_delete.length - 10}</Badge>
                            )}
                          </div>
                        </div>
                      )}
                      {m.plan.warnings?.length > 0 && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 p-2 space-y-0.5">
                          {m.plan.warnings.map((w, wi) => (
                            <p key={`w-${wi}`} className="text-[11px] text-amber-800 flex items-start gap-1">
                              <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" /> {w}
                            </p>
                          ))}
                        </div>
                      )}
                      {!m.applied ? (
                        <div className="flex gap-2 pt-1">
                          <Button
                            size="sm"
                            className="bg-[#238A92] hover:bg-[#0F5E66] text-white h-8"
                            disabled={confirmingId === m.plan.plan_id}
                            onClick={() => confirmPlan(i, m.plan)}
                            data-testid="availability-chat-confirm"
                          >
                            {confirmingId === m.plan.plan_id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
                            Conferma
                          </Button>
                          <Button size="sm" variant="outline" className="h-8" onClick={() => dismissPlan(i)} data-testid="availability-chat-dismiss">
                            <X size={14} className="mr-1" /> Annulla
                          </Button>
                        </div>
                      ) : (
                        <p className="text-xs text-[#1E7A58] font-medium flex items-center gap-1"><Check size={13} /> Applicato</p>
                      )}
                    </div>
                  )}
                  {m.plan && m.dismissed && !m.applied && (
                    <p className="mt-2 text-xs opacity-70">Operazione annullata.</p>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-[hsl(var(--secondary))] rounded-xl px-3.5 py-2.5 text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Sto elaborando la richiesta…
                </div>
              </div>
            )}
          </div>
        )}

        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); send(); }}
        >
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder='Es. "Tutti i martedì e giovedì di gennaio dalle 9 alle 12"'
            disabled={sending}
            data-testid="availability-chat-input"
          />
          <Button
            type="submit"
            disabled={sending || !input.trim()}
            className="bg-[#238A92] hover:bg-[#0F5E66] text-white flex-shrink-0"
            data-testid="availability-chat-send"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default AvailabilityChat;
